import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import TooltipComponent from "../common/Tooltip";
import { MOBILE_NAV_MQ } from "./lobbyPageStyles";
import { LOBBY_MAIN_CTA_CLIP } from "./LobbyMainCta";

const PLAY_CLIP = LOBBY_MAIN_CTA_CLIP;

const PLAY_PUNCH_MS = 150;

function triggerPlayHaptic() {
    try {
        if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
            navigator.vibrate(14);
        }
    } catch (_) {
        // Non-fatal.
    }
}

function PlayTrapezoidButton({
    tone = "ready",
    onClick,
}) {
    const disabled = tone !== "ready";
    const [isPunching, setIsPunching] = useState(false);
    const punchTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (punchTimerRef.current) {
                window.clearTimeout(punchTimerRef.current);
            }
        };
    }, []);

    const handleClick = (event) => {
        if (disabled) return;

        if (punchTimerRef.current) {
            window.clearTimeout(punchTimerRef.current);
        }
        setIsPunching(true);
        punchTimerRef.current = window.setTimeout(() => {
            setIsPunching(false);
            punchTimerRef.current = null;
        }, PLAY_PUNCH_MS);

        triggerPlayHaptic();
        onClick?.(event);
    };

    const frameBg =
        tone === "ready"
            ? `
                radial-gradient(
                    ellipse 78% 85% at 50% -8%,
                    rgba(255,247,215,0.92),
                    transparent 58%
                ),
                linear-gradient(
                    145deg,
                    #FFF0B3 0%,
                    #EDB64B 18%,
                    #A46513 58%,
                    #532804 100%
                )
            `
            : `
                linear-gradient(
                    145deg,
                    rgba(255, 223, 160, 0.3) 0%,
                    rgba(126, 82, 30, 0.38) 24%,
                    rgba(48, 35, 20, 0.72) 66%,
                    rgba(12, 13, 15, 0.96) 100%
                )
              `;

    const faceBg =
        tone === "ready"
            ? `
                radial-gradient(
                    ellipse 105% 92% at 50% -32%,
                    rgba(255,246,213,0.68) 0%,
                    rgba(255,211,116,0.24) 38%,
                    transparent 66%
                ),
                linear-gradient(
                    108deg,
                    transparent 12%,
                    rgba(255,244,211,0.04) 28%,
                    rgba(255,230,178,0.18) 44%,
                    rgba(255,204,101,0.04) 58%,
                    transparent 76%
                ),
                linear-gradient(
                    180deg,
                    #E0A43A 0%,
                    #C17A17 48%,
                    #824407 100%
                )
            `
            : `
                radial-gradient(
                    ellipse 92% 76% at 50% -24%,
                    rgba(232, 163, 61, 0.08),
                    transparent 62%
                ),
                repeating-linear-gradient(
                    90deg,
                    rgba(255,255,255,0.018) 0px,
                    rgba(255,255,255,0.018) 1px,
                    transparent 1px,
                    transparent 5px
                ),
                linear-gradient(
                    180deg,
                    #202124 0%,
                    #17181B 46%,
                    #0E0F11 100%
                )
            `;

    const faceShadow =
        tone === "ready"
            ? `
                inset 0 1px 0 rgba(255,250,230,0.74),
                inset 0 -2px 0 rgba(48,20,0,0.66),
                inset 0 0 0 1px rgba(255,208,112,0.28),
                inset 0 -15px 24px rgba(58,24,0,0.13)
            `
            : `
                inset 0 1px 0 rgba(255, 231, 185, 0.1),
                inset 0 -2px 0 rgba(0, 0, 0, 0.62),
                inset 0 0 0 1px rgba(255, 255, 255, 0.025),
                inset 0 -14px 24px rgba(0, 0, 0, 0.18)
              `;

    const outerShadow =
        tone === "ready"
            ? `
                0 0 23px rgba(232,163,61,0.44),
                0 14px 36px rgba(190,113,17,0.4),
                0 8px 20px rgba(0,0,0,0.52),
                inset 0 1px 0 rgba(255,248,222,0.58)
            `
            : `
                0 10px 24px rgba(0, 0, 0, 0.38),
                0 0 16px rgba(128, 82, 26, 0.1),
                inset 0 1px 0 rgba(255, 228, 176, 0.12)
              `;

    const labelColor =
        tone === "ready"
            ? "#180D03"
            : "rgba(221, 205, 178, 0.62)";

    return (
        <Box
            className={`play-wrap${isPunching ? " play-punch" : ""}`}
            sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: disabled ? 0.82 : 1,
                "@keyframes playPunchBloom": {
                    "0%": { opacity: 0.35 },
                    "35%": { opacity: 1 },
                    "100%": { opacity: 0.55 },
                },
                "&::before": {
                    content: '""',
                    position: "absolute",
                    width: "125%",
                    height: "155%",
                    borderRadius: "50%",
                    background:
                        tone === "ready"
                            ? "radial-gradient(ellipse, rgba(232,163,61,0.2), transparent 68%)"
                            : "transparent",
                    filter: "blur(12px)",
                    pointerEvents: "none",
                    transition: "opacity 0.15s ease-in-out",
                },
                "&.play-punch::before": {
                    background:
                        "radial-gradient(ellipse, rgba(255,214,120,0.42), transparent 70%)",
                    animation: "playPunchBloom 0.15s ease-out",
                },
            }}
        >
            <Box
                className={[
                    "btn-frame",
                    tone === "ready" ? "playButton" : "playButtonDisabled",
                    isPunching ? "is-punching" : "",
                ]
                    .filter(Boolean)
                    .join(" ")}
                sx={{
                    position: "relative",
                    zIndex: 1,
                    width: { xs: "13rem", xl: "14.75rem" },
                    height: { xs: "3.7rem", xl: "4.15rem" },
                    p: tone === "ready" ? "3px" : "2px",
                    clipPath: PLAY_CLIP,
                    WebkitClipPath: PLAY_CLIP,
                    background: frameBg,
                    boxShadow: outerShadow,
                    cursor: disabled ? "not-allowed" : "pointer",
                    transition: `
                        box-shadow 0.15s ease-in-out,
                        filter 0.15s ease-in-out
                    `,
                    [MOBILE_NAV_MQ]: {
                        width: "11.5rem",
                        height: "3.35rem",
                    },
                    ...(tone === "ready"
                        ? {
                              "&:hover": {
                                  filter: "brightness(1.11) saturate(1.08)",
                                  boxShadow: `
                                      0 0 24px rgba(232,163,61,0.44),
                                      0 16px 42px rgba(174,100,12,0.4),
                                      0 10px 24px rgba(0,0,0,0.55),
                                      inset 0 1px 0 rgba(255,244,210,0.52)
                                  `,
                                  "& .btn-play": {
                                      boxShadow: `
                                          inset 0 1px 0 rgba(255,244,210,0.7),
                                          inset 0 -2px 0 rgba(45,20,0,0.72),
                                          inset 0 0 0 1px rgba(255,207,110,0.3),
                                          inset 0 -18px 28px rgba(48,20,0,0.15)
                                      `,
                                  },
                                  "& .play-shine": {
                                      opacity: 0.7,
                                  },
                              },
                              "&:active, &.is-punching": {
                                  filter: "brightness(0.93) saturate(1.05)",
                                  boxShadow: `
                                      0 0 18px rgba(232,163,61,0.5),
                                      0 4px 12px rgba(0,0,0,0.42),
                                      inset 0 1px 0 rgba(255,236,190,0.28)
                                  `,
                                  "& .btn-play": {
                                      filter: "brightness(0.96)",
                                      boxShadow: `
                                          inset 0 3px 10px rgba(40,16,0,0.55),
                                          inset 0 -1px 0 rgba(255,218,137,0.22),
                                          inset 0 0 0 1px rgba(255,196,90,0.18)
                                      `,
                                  },
                                  "& .play-shine": {
                                      opacity: 0.28,
                                  },
                                  "& .label": {
                                      textShadow: `
                                          0 1px 0 rgba(255, 231, 168, 0.28),
                                          0 -1px 1px rgba(20, 7, 0, 0.9)
                                      `,
                                  },
                              },
                          }
                        : {}),
                }}
            >
                <Box
                    component="button"
                    type="button"
                    disabled={disabled}
                    onClick={disabled ? undefined : handleClick}
                    className="btn-play"
                    sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        border: "none",
                        padding: 0,
                        margin: 0,
                        clipPath: PLAY_CLIP,
                        WebkitClipPath: PLAY_CLIP,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        cursor: "inherit",
                        outline: "none",
                        WebkitTapHighlightColor: "transparent",
                        background: faceBg,
                        boxShadow: faceShadow,
                        transition: "box-shadow 0.15s ease-in-out, filter 0.15s ease-in-out",
                        "&:focus-visible": {
                            outline: `2px solid rgba(255, 232, 174, 0.92)`,
                            outlineOffset: "-5px",
                        },
                    }}
                >
                    {tone === "ready" && (
                        <>
                            <Box
                                aria-hidden
                                className="play-shine"
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    background: `
                                        linear-gradient(
                                            112deg,
                                            transparent 8%,
                                            rgba(255,250,232,0.06) 26%,
                                            rgba(255,242,207,0.4) 48%,
                                            rgba(255,218,143,0.08) 68%,
                                            transparent 88%
                                        )
                                    `,
                                    opacity: 0.52,
                                    transition: "opacity 180ms ease-in-out",
                                    pointerEvents: "none",
                                    zIndex: 1,
                                }}
                            />
                            <Box
                                aria-hidden
                                sx={{
                                    position: "absolute",
                                    left: "10%",
                                    right: "10%",
                                    top: 0,
                                    height: "1px",
                                    background: `
                                        linear-gradient(
                                            90deg,
                                            transparent,
                                            rgba(255,244,210,0.9),
                                            transparent
                                        )
                                    `,
                                    boxShadow: "0 1px 7px rgba(255,214,126,0.28)",
                                    pointerEvents: "none",
                                    zIndex: 1,
                                }}
                            />
                            <Box
                                aria-hidden
                                sx={{
                                    position: "absolute",
                                    left: "16%",
                                    right: "16%",
                                    bottom: 0,
                                    height: "1px",
                                    background: `
                                        linear-gradient(
                                            90deg,
                                            transparent,
                                            rgba(71,29,0,0.82),
                                            transparent
                                        )
                                    `,
                                    pointerEvents: "none",
                                    zIndex: 1,
                                }}
                            />
                        </>
                    )}
                    <Box
                        component="span"
                        className="label"
                        sx={{
                            position: "relative",
                            zIndex: 2,
                            fontFamily: "'Orbitron', sans-serif",
                            fontWeight: tone === "missing" ? 700 : 900,
                            fontSize:
                                tone === "missing"
                                    ? { xs: "1.25rem", xl: "1.55rem" }
                                    : { xs: "1.4rem", xl: "1.6rem" },
                            letterSpacing: tone === "missing" ? "1px" : "0.14em",
                            textTransform: "uppercase",
                            color: labelColor,
                            lineHeight: 1,
                            WebkitTextStroke:
                                tone === "ready"
                                    ? "0.5px rgba(24, 8, 0, 0.58)"
                                    : tone === "missing"
                                      ? "0.35px rgba(0, 0, 0, 0.48)"
                                      : "0",
                            textShadow:
                                tone === "ready"
                                    ? `
                                        0 -1px 1px rgba(20, 7, 0, 0.82),
                                        0 1px 0 rgba(255, 231, 168, 0.48),
                                        0 2px 2px rgba(255, 196, 82, 0.12)
                                    `
                                    : tone === "missing"
                                      ? `
                                          0 -1px 1px rgba(0, 0, 0, 0.82),
                                          0 1px 0 rgba(255, 235, 203, 0.1)
                                      `
                                      : "none",
                            [MOBILE_NAV_MQ]: {
                                fontSize:
                                    tone === "missing" ? "1.05rem" : "1.12rem",
                                letterSpacing:
                                    tone === "missing" ? "0.08em" : "0.12em",
                            },
                        }}
                    >
                        play
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default function LobbyQueueButton({
    lobbyState,
    matchmakingStarted,
    isLeader,
    hasRequiredQueueSelections,
    currentLobbyCode,
    hasSelectedMaps,
    hasSelectedServerLocations,
    startLobbyMatchmakingByLeader,
    isAccountAllowedToPlay,
    isQueueEffectivelyClosed,
    startSoloMatchmaking,
}) {
    const playTone =
        !hasRequiredQueueSelections ||
        !isAccountAllowedToPlay ||
        isQueueEffectivelyClosed
            ? "blocked"
            : "ready";

    if (lobbyState === "searching" || (!isLeader && matchmakingStarted)) {
        return null;
    }

    return (
        <Box
            className="queueButtonCont"
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                width: "100%",
                [MOBILE_NAV_MQ]: {
                    px: "0.75rem",
                    boxSizing: "border-box",
                },
            }}
        >
            {lobbyState === "standby" &&
            !hasRequiredQueueSelections &&
            (isLeader || !currentLobbyCode) ? (
                <TooltipComponent
                    title={
                        !hasSelectedMaps && !hasSelectedServerLocations
                            ? "Before Play select at least one map and one server location"
                            : !hasSelectedMaps
                              ? "Before Play select at least one map"
                              : "Before Play select at least one server location"
                    }
                    placement="bottom"
                >
                    <span>
                        <PlayTrapezoidButton tone="missing" />
                    </span>
                </TooltipComponent>
            ) : lobbyState === "standby" && isLeader && currentLobbyCode ? (
                <PlayTrapezoidButton
                    tone={playTone}
                    onClick={startLobbyMatchmakingByLeader}
                />
            ) : lobbyState === "standby" && !currentLobbyCode ? (
                <PlayTrapezoidButton
                    tone={playTone}
                    onClick={startSoloMatchmaking}
                />
            ) : null}
        </Box>
    );
}
